// /campus/students — roster of the caller's school's students with
// search / filter / verification-status chips and vouch+flag actions
// that pipe into the existing student-verification review queue from
// Prompt 8. Campus_admin CANNOT auto-verify; the vouch signal is
// surfaced to Coravu admins for final approval.

import { useFocusEffect } from "expo-router";
import { COLORS } from "../../../lib/brand";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import {
  flagStudent,
  listMyStudentsWithVerification,
  vouchStudent,
  type CampusStudentDetail,
} from "../../../lib/campus";

const BLACK = COLORS.BLACK.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = COLORS.AMBER.hex;
const SAGE = COLORS.SAGE.hex;
const LINEN = COLORS.LINEN.hex;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = COLORS.RED_ERROR.hex;
const BORDER = "rgba(255,255,255,0.07)";

type VerifyFilter = "all" | "verified" | "pending" | "self_attested";

export default function CampusStudentsScreen() {
  const [rows, setRows] = useState<CampusStudentDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [verifyFilter, setVerifyFilter] = useState<VerifyFilter>("all");
  const [majorFilter, setMajorFilter] = useState<string>("all");
  const [gradFilter, setGradFilter] = useState<string>("all");
  const [toast, setToast] = useState<string | null>(null);
  const [flagOpen, setFlagOpen] = useState<CampusStudentDetail | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const r = await listMyStudentsWithVerification();
      setRows(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const majors = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) if (r.major?.trim()) s.add(r.major.trim());
    return [...s].sort();
  }, [rows]);

  const gradYears = useMemo(() => {
    const s = new Set<number>();
    for (const r of rows) if (r.graduation_year) s.add(r.graduation_year);
    return [...s].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const hay = `${r.full_name ?? ""} ${r.email ?? ""} ${r.major ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (verifyFilter === "verified" && !r.school_verified) return false;
      if (verifyFilter === "pending" && r.school_verified) return false;
      if (verifyFilter === "self_attested" && r.school_verification_method !== "self_attested") return false;
      if (majorFilter !== "all" && r.major !== majorFilter) return false;
      if (gradFilter !== "all" && String(r.graduation_year ?? "") !== gradFilter) return false;
      return true;
    });
  }, [rows, search, verifyFilter, majorFilter, gradFilter]);

  const stats = useMemo(() => {
    const verified = rows.filter((r) => r.school_verified).length;
    return { verified, pending: rows.length - verified };
  }, [rows]);

  async function onVouch(student: CampusStudentDetail) {
    try {
      await vouchStudent(student.student_id);
      setToast(`Vouched for ${student.full_name ?? "student"}. Coravu admin will review.`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Vouch failed");
    }
  }

  return (
    <View style={{ padding: 24, maxWidth: 1080 }}>
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: AMBER }}>
        Campus · Students
      </Text>
      <Text style={{ marginTop: 6, fontFamily: "CormorantGaramond_300Light", fontSize: 28, lineHeight: 32, color: LINEN }}>
        Students from your school
      </Text>
      <Text style={{ marginTop: 4, fontFamily: "DMSans_300Light", fontSize: 13, color: WM }}>
        {rows.length} student{rows.length === 1 ? "" : "s"} on Coravu — {stats.verified} verified, {stats.pending} pending
      </Text>

      {err ? <ErrorBanner>{err}</ErrorBanner> : null}
      {toast ? (
        <View style={{ marginTop: 12, padding: 10, borderRadius: 4, borderWidth: 1, borderColor: "rgba(143,187,138,0.40)", backgroundColor: "rgba(143,187,138,0.08)" }}>
          <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12, color: SAGE }}>{toast}</Text>
        </View>
      ) : null}

      {/* Filter bar */}
      <View style={{ marginTop: 18, padding: 12, borderRadius: 6, backgroundColor: FOREST_MID, borderWidth: 1, borderColor: BORDER, gap: 10 }}>
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search name, email, major…"
          placeholderTextColor={WL}
          style={
            {
              backgroundColor: BLACK,
              borderWidth: 1,
              borderColor: BORDER,
              borderRadius: 4,
              paddingHorizontal: 12,
              paddingVertical: 9,
              color: W,
              fontFamily: "DMSans_400Regular",
              fontSize: 13,
              ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
            } as unknown as TextStyle
          }
        />
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, color: WM }}>Verification:</Text>
          <Chip label="All" active={verifyFilter === "all"} onPress={() => setVerifyFilter("all")} />
          <Chip label="Verified" active={verifyFilter === "verified"} onPress={() => setVerifyFilter("verified")} />
          <Chip label="Pending" active={verifyFilter === "pending"} onPress={() => setVerifyFilter("pending")} />
          <Chip label="Self-attested" active={verifyFilter === "self_attested"} onPress={() => setVerifyFilter("self_attested")} />
        </View>
        {majors.length > 0 ? (
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, color: WM }}>Major:</Text>
            <Chip label="All" active={majorFilter === "all"} onPress={() => setMajorFilter("all")} />
            {majors.slice(0, 12).map((m) => (
              <Chip key={m} label={m} active={majorFilter === m} onPress={() => setMajorFilter(m)} />
            ))}
          </View>
        ) : null}
        {gradYears.length > 0 ? (
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, color: WM }}>Grad year:</Text>
            <Chip label="All" active={gradFilter === "all"} onPress={() => setGradFilter("all")} />
            {gradYears.map((y) => (
              <Chip key={y} label={String(y)} active={gradFilter === String(y)} onPress={() => setGradFilter(String(y))} />
            ))}
          </View>
        ) : null}
      </View>

      {/* List */}
      {loading ? (
        <ActivityIndicator style={{ marginTop: 20 }} size="small" color={AMBER} />
      ) : filtered.length === 0 ? (
        <View style={{ marginTop: 18, padding: 22, borderRadius: 6, backgroundColor: FOREST_MID, borderWidth: 1, borderColor: BORDER, alignItems: "center" }}>
          <Text style={{ fontFamily: "CormorantGaramond_500Medium", fontSize: 18, color: LINEN }}>
            No students match.
          </Text>
          <Text style={{ marginTop: 4, fontFamily: "DMSans_400Regular", fontSize: 12, color: WM }}>
            {rows.length === 0 ? "No students from your school on Coravu yet." : "Try a different filter."}
          </Text>
        </View>
      ) : (
        <View style={{ marginTop: 18, gap: 8 }}>
          {filtered.map((row) => (
            <StudentRow
              key={row.student_id}
              row={row}
              onVouch={() => void onVouch(row)}
              onFlag={() => setFlagOpen(row)}
            />
          ))}
        </View>
      )}

      {flagOpen ? (
        <FlagModal
          student={flagOpen}
          onClose={() => setFlagOpen(null)}
          onDone={async (reason) => {
            try {
              await flagStudent(flagOpen.student_id, reason);
              setToast(`Flagged ${flagOpen.full_name ?? "student"} — Coravu admin alerted.`);
              setFlagOpen(null);
              await load();
            } catch (e) {
              setErr(e instanceof Error ? e.message : "Flag failed");
            }
          }}
        />
      ) : null}
    </View>
  );
}

function StudentRow({
  row,
  onVouch,
  onFlag,
}: {
  row: CampusStudentDetail;
  onVouch: () => void;
  onFlag: () => void;
}) {
  return (
    <View style={{ padding: 14, borderRadius: 6, backgroundColor: FOREST_MID, borderWidth: 1, borderColor: BORDER }}>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <View style={{ flex: 1, minWidth: 200 }}>
          <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 14, color: LINEN }}>{row.full_name ?? "Student"}</Text>
          <Text style={{ marginTop: 2, fontFamily: "DMSans_400Regular", fontSize: 11.5, color: WS }}>{row.email ?? "—"}</Text>
          <Text style={{ marginTop: 2, fontFamily: "DMSans_400Regular", fontSize: 11, color: WM }}>
            {row.major ?? "—"} · {row.graduation_year ?? "—"} · {row.total_competitions} case{row.total_competitions === 1 ? "" : "s"}
          </Text>
          <View style={{ marginTop: 6, flexDirection: "row", flexWrap: "wrap", gap: 4 }}>
            {row.school_verified ? (
              <Pill color={SAGE}>{`Verified (${row.school_verification_method ?? "—"})`}</Pill>
            ) : row.school_verification_method === "self_attested" ? (
              <Pill color={AMBER}>Self-attested</Pill>
            ) : (
              <Pill color={WM}>No verification</Pill>
            )}
            {row.vouched_by_me ? <Pill color={SAGE}>You vouched</Pill> : null}
            {row.flagged_by_me ? <Pill color={RED}>You flagged</Pill> : null}
          </View>
        </View>
        <View style={{ flexDirection: "row", gap: 6 }}>
          {!row.school_verified && !row.vouched_by_me ? (
            <SmallBtn label="✓ Vouch for" tint={SAGE} onPress={onVouch} />
          ) : null}
          {!row.flagged_by_me ? <SmallBtn label="✗ Flag" tint={RED} onPress={onFlag} /> : null}
        </View>
      </View>
    </View>
  );
}

function FlagModal({
  student,
  onClose,
  onDone,
}: {
  student: CampusStudentDetail;
  onClose: () => void;
  onDone: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
        <View style={{ maxWidth: 480, width: "100%", alignSelf: "center", backgroundColor: FOREST_MID, borderRadius: 8, borderWidth: 1, borderColor: BORDER, padding: 20 }}>
          <Text style={{ fontFamily: "CormorantGaramond_500Medium", fontSize: 22, color: LINEN }}>Flag student</Text>
          <Text style={{ marginTop: 4, fontFamily: "DMSans_400Regular", fontSize: 12, color: WM }}>
            Flag {student.full_name ?? "this student"} for Coravu admin review. They won&apos;t be removed; an admin
            will double-check whether they actually attend your school.
          </Text>
          <TextInput
            value={reason}
            onChangeText={setReason}
            placeholder="Why are you flagging this student?"
            placeholderTextColor={WL}
            multiline
            style={
              {
                marginTop: 12,
                minHeight: 80,
                backgroundColor: BLACK,
                borderWidth: 1,
                borderColor: BORDER,
                borderRadius: 4,
                paddingHorizontal: 12,
                paddingVertical: 10,
                color: W,
                fontFamily: "DMSans_400Regular",
                fontSize: 13,
                ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
              } as unknown as TextStyle
            }
          />
          <View style={{ marginTop: 14, flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
            <SmallBtn label="Cancel" onPress={onClose} />
            <SmallBtn
              label={busy ? "Flagging…" : "Submit flag"}
              tint={RED}
              onPress={async () => {
                if (!reason.trim() || busy) return;
                setBusy(true);
                try {
                  await onDone(reason.trim());
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy || !reason.trim()}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

function Chip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 10,
          paddingVertical: 5,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? AMBER : BORDER,
          backgroundColor: active ? "rgba(181,135,58,0.12)" : "transparent",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, color: active ? AMBER : WS }}>{label}</Text>
    </Pressable>
  );
}

function Pill({ children, color }: { children: string; color: string }) {
  return (
    <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, borderWidth: 1, borderColor: color }}>
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", color }}>
        {children}
      </Text>
    </View>
  );
}

function SmallBtn({
  label,
  onPress,
  tint,
  disabled,
}: {
  label: string;
  onPress: () => void;
  tint?: string;
  disabled?: boolean;
}) {
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 7,
          borderRadius: 3,
          borderWidth: 1,
          borderColor: disabled ? BORDER : tint ?? BORDER,
          backgroundColor: tint && !disabled ? "rgba(181,135,58,0.10)" : "rgba(255,255,255,0.02)",
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web" && !disabled ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 10.5, letterSpacing: 1.2, color: tint ?? WS }}>{label}</Text>
    </Pressable>
  );
}

function ErrorBanner({ children }: { children: string }) {
  return (
    <View style={{ marginTop: 12, padding: 10, borderRadius: 4, borderWidth: 1, borderColor: "rgba(220,80,80,0.40)", backgroundColor: "rgba(220,80,80,0.08)" }}>
      <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12, color: RED }}>{children}</Text>
    </View>
  );
}
